import React, { useState, useEffect, useRef } from 'react';
import { 
    Button, 
    Card, 
    Col, 
    Row, 
    Table, 
    Form, 
    Badge, 
    Alert, 
    Spinner,
    ProgressBar,
    Modal,
    Dropdown
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCog,
    faRobot,
    faCircleDot,
    faUserTie,
    faBullseye,
    faClock,
    faSync,
    faInfoCircle,
    faNetworkWired,
    faDatabase,
    faDownload,
    faEye,
    faChartBar,
    faExclamationTriangle,
    faCheck,
    faServer,
    faShieldAlt,
    faHistory,
    faBolt,
    faTools,
    faGlobe,
    faSave,
    faUndo,
    faFileExport,
    faFileImport,
    faTrashAlt,
    faWarning,
    faCheckCircle,
    faTimesCircle,
    faSpinner
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { 
    SellerbotService, 
    useSellerbotStatus,
    formatDate
} from '../../service/sellerbotService';
import { getHatateToken } from '../../helpers/utils';

// ==================== 🎯 INTERFACES ====================

interface SettingsTabProps {
    language: 'es' | 'en';
    onSettingsChange?: (settings: any) => void;
    // Props para recibir datos de otras pestañas
    totalConversations?: number;
    totalProducts?: number;
    totalProfiles?: number;
    totalCampaigns?: number;
    botConversations?: any[];
}

interface SystemSettings {
    language: 'es' | 'en';
    autoRefresh: boolean;
    globalAutoRefresh: boolean;
    refreshInterval: number;
    notificationsEnabled: boolean;
    soundEnabled: boolean;
    theme: 'light' | 'dark' | 'auto';
}

interface ServiceStatus {
    name: string;
    status: 'online' | 'offline' | 'checking' | 'error';
    endpoint: string;
    lastCheck: string;
    responseTime?: number;
    details?: any | null;
    message?: string;
}

// ==================== 🎨 ESTILOS CSS ====================
const customStyles = `
  .settings-tab .settings-card {
    transition: all 0.3s ease !important;
    border: 1px solid #e9ecef !important;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
  }

  .settings-tab .settings-card:hover {
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
    transform: translateY(-2px) !important;
  }

  .settings-tab .service-status-online {
    background: linear-gradient(135deg, #d4edda, #c3e6cb);
    border-left: 4px solid #28a745;
  }

  .settings-tab .service-status-offline {
    background: linear-gradient(135deg, #f8d7da, #f1b0b7);
    border-left: 4px solid #dc3545;
  }

  .settings-tab .service-status-checking {
    background: linear-gradient(135deg, #d1ecf1, #bee5eb);
    border-left: 4px solid #17a2b8;
  }

  .settings-tab .service-status-error {
    background: linear-gradient(135deg, #fff3cd, #ffeaa7);
    border-left: 4px solid #ffc107;
  }

  .settings-tab .control-panel {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border-radius: 12px;
    padding: 20px;
    border: 2px solid #dee2e6;
  }

  .settings-tab .system-metric {
    background: white;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 10px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
  }

  .settings-tab .system-metric:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  .settings-tab .danger-zone {
    background: linear-gradient(135deg, #f8d7da, #f1b0b7);
    border: 2px solid #dc3545;
    border-radius: 12px;
    padding: 20px;
  }

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0.3; }
  }

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }

  .settings-tab .status-indicator {
    display: inline-block;
    border-radius: 50%;
  }

  .settings-tab .status-checking {
    animation: pulse 1.5s infinite;
  }

  .settings-tab .service-details {
    font-family: 'Courier New', monospace;
    font-size: 0.8rem;
    background: #f8f9fa;
    padding: 8px;
    border-radius: 4px;
    margin-top: 8px;
    border-left: 3px solid #007bff;
  }

  @media (max-width: 768px) {
    .settings-tab .settings-card:hover {
      transform: none;
    }
  }
`;

// ==================== 🎯 COMPONENTE PRINCIPAL ====================
const SettingsTab: React.FC<SettingsTabProps> = ({ 
    language, 
    onSettingsChange,
    totalConversations = 0,
    totalProducts = 0,
    totalProfiles = 0,
    totalCampaigns = 0,
    botConversations = []
}) => {
    
    // ==================== HOOKS DE DATOS ====================
    const { 
        status: sellerbotStatus, 
        loading: statusLoading, 
        error: statusError, 
        refetch: refetchStatus 
    } = useSellerbotStatus();

    // ==================== ESTADOS LOCALES ====================
    const [systemSettings, setSystemSettings] = useState<SystemSettings>({
        language,
        autoRefresh: true,
        globalAutoRefresh: true,
        refreshInterval: 30,
        notificationsEnabled: true,
        soundEnabled: false,
        theme: 'light'
    });

    const [services, setServices] = useState<ServiceStatus[]>([
        {
            name: 'Sales Bot API',
            status: 'offline',
            endpoint: 'sellerbot/status',
            lastCheck: new Date().toISOString()
        },
        {
            name: 'WhatsApp Bot Service',
            status: 'offline',
            endpoint: 'whatsapp/bot/status',
            lastCheck: new Date().toISOString()
        },
        {
            name: 'LLM Service',
            status: 'offline',
            endpoint: 'bot/status',
            lastCheck: new Date().toISOString()
        }
    ]);

    const [isRestarting, setIsRestarting] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showDangerModal, setShowDangerModal] = useState(false);
    const [dangerAction, setDangerAction] = useState<string>('');

    // ==================== CONFIGURACIÓN DEL BOT ====================
    const salesBotConfig = sellerbotStatus?.data || {
        enabled: false,
        activeProfiles: 0,
        runningCampaigns: 0,
        version: '1.0.0',
        lastSync: new Date().toISOString(),
        port: 50001,
        baseDirectory: '',
        databasePath: ''
    };

    const isBotConnected = sellerbotStatus?.success || true;

    // ==================== FUNCIONES DE TRADUCCIÓN ====================
    const getText = (key: string): string => {
        const texts = {
            es: {
                title: 'Configuración del Sistema',
                mainControl: 'Control Principal',
                botAgent: 'Sales Bot Agent',
                botAgentDesc: 'Servicio principal de ventas automatizadas con IA',
                activeProfiles: 'Perfiles IA Activos',
                activeProfilesDesc: 'Vendedores virtuales ejecutándose actualmente',
                runningCampaigns: 'Campañas en Ejecución',
                runningCampaignsDesc: 'Campañas automatizadas ejecutándose actualmente',
                automatedRoutines: 'Rutinas Automatizadas',
                automatedRoutinesDesc: 'Tareas programadas y procesos automáticos',
                autoRefresh: 'Auto-actualización',
                autoRefreshDesc: 'Actualización automática de datos cada 30 segundos',
                globalAutoRefresh: 'Auto-actualización Global',
                globalAutoRefreshDesc: 'Control maestro para todas las actualizaciones automáticas',
                advancedConfig: 'Configuración Avanzada',
                systemLanguage: 'Idioma del Sistema',
                serverPort: 'Puerto del Servidor',
                serverPortDesc: 'Puerto donde corre el servicio de Sales Bot',
                dataDirectory: 'Directorio de Datos',
                dataDirectoryDesc: 'Ubicación donde se almacenan los datos del SellerBot',
                serviceStatus: 'Estado del Servicio',
                online: 'En línea',
                offline: 'Fuera de línea',
                checking: 'Verificando',
                error: 'Error',
                port: 'Puerto',
                server: 'Servidor',
                version: 'Versión',
                lastSync: 'Última sync',
                checkStatus: 'Verificar Estado',
                refreshData: 'Actualizar Datos',
                quickStats: 'Estadísticas Rápidas',
                activeConversations: 'Conversaciones Activas',
                activeProducts: 'Productos Activos',
                workingProfiles: 'Perfiles Trabajando',
                runningCampaignsShort: 'Campañas Ejecutándose',
                systemSettings: 'Configuración del Sistema',
                notifications: 'Notificaciones',
                sound: 'Sonido',
                theme: 'Tema',
                refreshInterval: 'Intervalo de Actualización',
                seconds: 'segundos',
                save: 'Guardar',
                reset: 'Restablecer',
                export: 'Exportar',
                import: 'Importar',
                dangerZone: 'Zona de Peligro',
                resetSystem: 'Restablecer Sistema',
                clearDatabase: 'Limpiar Base de Datos',
                factoryReset: 'Reset de Fábrica',
                serviceHealth: 'Salud de Servicios',
                checkAllServices: 'Verificar Todos los Servicios',
                lastChecked: 'Última verificación',
                responseTime: 'Tiempo de respuesta',
                active: 'activo',
                disabled: 'desactivado',
                enabled: 'activado',
                profiles: 'perfiles',
                campaigns: 'campañas',
                routines: 'rutinas',
                activas: 'activas',
                view: 'Ver',
                manualRefresh: 'Refresh Manual',
                light: 'Claro',
                dark: 'Oscuro',
                auto: 'Automático',
                endpoint: 'Endpoint',
                details: 'Detalles'
            },
            en: {
                title: 'System Configuration',
                mainControl: 'Main Control',
                botAgent: 'Sales Bot Agent',
                botAgentDesc: 'Main AI-powered automated sales service',
                activeProfiles: 'Active AI Profiles',
                activeProfilesDesc: 'Virtual salespeople currently running',
                runningCampaigns: 'Running Campaigns',
                runningCampaignsDesc: 'Automated campaigns currently running',
                automatedRoutines: 'Automated Routines',
                automatedRoutinesDesc: 'Scheduled tasks and automatic processes',
                autoRefresh: 'Auto-refresh',
                autoRefreshDesc: 'Automatic data refresh every 30 seconds',
                globalAutoRefresh: 'Global Auto-refresh',
                globalAutoRefreshDesc: 'Master control for all automatic updates',
                advancedConfig: 'Advanced Configuration',
                systemLanguage: 'System Language',
                serverPort: 'Server Port',
                serverPortDesc: 'Port where Sales Bot service is running',
                dataDirectory: 'Data Directory',
                dataDirectoryDesc: 'Location where SellerBot data is stored',
                serviceStatus: 'Service Status',
                online: 'Online',
                offline: 'Offline',
                checking: 'Checking',
                error: 'Error',
                port: 'Port',
                server: 'Server',
                version: 'Version',
                lastSync: 'Last sync',
                checkStatus: 'Check Status',
                refreshData: 'Refresh Data',
                quickStats: 'Quick Stats',
                activeConversations: 'Active Conversations',
                activeProducts: 'Active Products',
                workingProfiles: 'Working Profiles',
                runningCampaignsShort: 'Running Campaigns',
                systemSettings: 'System Settings',
                notifications: 'Notifications',
                sound: 'Sound',
                theme: 'Theme',
                refreshInterval: 'Refresh Interval',
                seconds: 'seconds',
                save: 'Save',
                reset: 'Reset',
                export: 'Export',
                import: 'Import',
                dangerZone: 'Danger Zone',
                resetSystem: 'Reset System',
                clearDatabase: 'Clear Database',
                factoryReset: 'Factory Reset',
                serviceHealth: 'Service Health',
                checkAllServices: 'Check All Services',
                lastChecked: 'Last checked',
                responseTime: 'Response time',
                active: 'active',
                disabled: 'disabled',
                enabled: 'enabled',
                profiles: 'profiles',
                campaigns: 'campaigns',
                routines: 'routines',
                activas: 'active',
                view: 'View',
                manualRefresh: 'Manual Refresh',
                light: 'Light',
                dark: 'Dark',
                auto: 'Auto',
                endpoint: 'Endpoint',
                details: 'Details'
            }
        };
        return texts[language]?.[key] || key;
    };

    // ==================== FUNCIONES DE HEALTH CHECK ====================

// ==================== 🔧 HEALTH CHECK SIMPLE - MISMO PATRÓN QUE SALES BOT API ====================

const checkServiceHealth = async (service: ServiceStatus): Promise<ServiceStatus> => {
    const startTime = Date.now();
    
    try {
        const token = getHatateToken();
        
        // Debug del token
        console.log(`🔍 Token for ${service.name}:`, {
            hasToken: !!token,
            tokenType: typeof token,
            tokenLength: token?.length,
            tokenPreview: token ? `${token.substring(0, 20)}...` : 'No token',
            fullToken: token // TEMPORAL - para ver qué está devolviendo
        });
        
        console.log(`🔍 Checking service: ${service.name}`);

        // URLs exactas
        let fullUrl: string;
        switch (service.name) {
            case 'Sales Bot API':
                fullUrl = 'https://backend.anubisai.net/api/sellerbot/status';
                break;
            case 'WhatsApp Bot Service':
                fullUrl = 'https://backend.anubisai.net/api/whatsapp/bot/status';
                break;
            case 'LLM Service':
                fullUrl = 'https://backend.anubisai.net/api/bot/status';
                break;
            default:
                fullUrl = `https://backend.anubisai.net/api/${service.endpoint}`;
        }
        
        console.log(`🌐 Making request to: ${fullUrl}`);

        // Headers con API keys específicas
        const headers: any = {};
        
        if (service.name === 'Sales Bot API') {
            // SellerBot: usar el status existente en lugar de hacer request
            // Ya tenemos sellerbotStatus disponible
            const responseTime = 10; // Simulado porque no hay request real
            
            if (sellerbotStatus?.success) {
                return {
                    ...service,
                    status: 'online',
                    lastCheck: new Date().toISOString(),
                    responseTime,
                    message: `Sales Bot ${sellerbotStatus.data?.enabled ? 'habilitado' : 'deshabilitado'}`,
                    details: {
                        enabled: sellerbotStatus.data?.enabled,
                        version: sellerbotStatus.data?.version,
                        activeProfiles: sellerbotStatus.data?.activeProfiles,
                        runningCampaigns: sellerbotStatus.data?.runningCampaigns
                    }
                };
            } else {
                return {
                    ...service,
                    status: 'offline',
                    lastCheck: new Date().toISOString(),
                    responseTime,
                    message: 'Sales Bot no disponible',
                    details: null
                };
            }
        } else if (service.name === 'WhatsApp Bot Service' && token) {
            // WhatsApp Bot usa el hatatetoken
            if (token.startsWith('Bearer ')) {
                headers['x-api-key'] = token.substring(7); // Solo el JWT sin "Bearer "
                headers['Authorization'] = token; // Con "Bearer " incluido
            } else {
                headers['x-api-key'] = token;
                headers['Authorization'] = `Bearer ${token}`;
            }
        }
        // LLM Service no necesita headers (público)

        // Usar fetch como en tu componente funcional
        const response = await fetch(fullUrl, {
            headers
        });
        
        const responseData = await response.json();
        const responseTime = Date.now() - startTime;
        
        console.log(`✅ ${service.name} response:`, responseData);

        // Determinar estado basado en la respuesta
        let status: 'online' | 'offline' | 'error' = 'offline';
        let message = '';
        let details: any | null = null;

        if (response.ok && responseData) {
            if (service.name === 'WhatsApp Bot Service') {
                const { success, botConnected, botEnabled, message: responseMessage } = responseData;
                if (success && botConnected && botEnabled) {
                    status = 'online';
                    message = responseMessage || 'WhatsApp Bot funcionando correctamente';
                    details = {
                        botConnected: botConnected,
                        botEnabled: botEnabled,
                        type: responseData.botConnected?.type || 'unknown'
                    };
                } else {
                    status = 'error';
                    message = `Bot conectado: ${botConnected}, Bot habilitado: ${botEnabled}`;
                    details = responseData;
                }
            } else if (service.name === 'Sales Bot API') {
                const { success, data } = responseData;
                if (success && data) {
                    status = 'online';
                    message = `Sales Bot ${data.enabled ? 'habilitado' : 'deshabilitado'}`;
                    details = {
                        enabled: data.enabled,
                        version: data.version,
                        activeProfiles: data.activeProfiles,
                        runningCampaigns: data.runningCampaigns
                    };
                } else {
                    status = 'error';
                    message = 'Sales Bot API responde pero con errores';
                    details = responseData;
                }
            } else if (service.name === 'LLM Service') {
                // Para LLM Service: cualquier respuesta 200 es buena
                if (response.ok) {
                    status = 'online';
                    message = responseData?.message || 'LLM Service funcionando correctamente';
                    details = responseData;
                } else {
                    status = 'error';
                    message = 'LLM Service responde pero con errores';
                    details = responseData;
                }
            }
        }

        return {
            ...service,
            status,
            lastCheck: new Date().toISOString(),
            responseTime,
            message,
            details
        };

    } catch (error: any) {
        console.error(`❌ Error checking ${service.name}:`, error);
        
        const responseTime = Date.now() - startTime;
        let errorMessage = 'Servicio no disponible';
        
        // Para fetch, el error puede ser diferente
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
            errorMessage = 'Error de conexión';
        } else {
            errorMessage = error.message || 'Servicio no disponible';
        }
        
        return {
            ...service,
            status: 'offline',
            lastCheck: new Date().toISOString(),
            responseTime,
            message: errorMessage,
            details: null
        };
    }
};

const checkAllServices = async () => {
    setIsRestarting(true);
    
    // Marcar todos los servicios como "checking"
    setServices(prevServices => 
        prevServices.map(service => ({
            ...service,
            status: 'checking' as const
        }))
    );

    try {
        const servicePromises = services.map(service => checkServiceHealth(service));
        const updatedServices = await Promise.all(servicePromises);
        
        setServices(updatedServices);
        
        const onlineServices = updatedServices.filter(s => s.status === 'online').length;
        const totalServices = updatedServices.length;
        
        setSuccess(
            language === 'es' 
                ? `Servicios verificados: ${onlineServices}/${totalServices} en línea`
                : `Services checked: ${onlineServices}/${totalServices} online`
        );
        setTimeout(() => setSuccess(null), 3000);
        
    } catch (error: any) {
        console.error('Error during service check:', error);
        setError(language === 'es' ? 'Error verificando servicios' : 'Error checking services');
        setTimeout(() => setError(null), 3000);
    } finally {
        setIsRestarting(false);
    }
};

    // ==================== OTRAS FUNCIONES ====================

    const handleSalesBotToggle = async () => {
        const newState = !salesBotConfig.enabled;
        setIsRestarting(true);
        setError(null);
        
        try {
            const response = await SellerbotService.toggleBot(newState, 'admin');
            
            if (response.success) {
                setTimeout(() => {
                    refetchStatus();
                    setIsRestarting(false);
                    setSuccess(
                        `Sales Bot ${newState ? getText('enabled') : getText('disabled')} ${language === 'es' ? 'correctamente' : 'successfully'}`
                    );
                    setTimeout(() => setSuccess(null), 3000);
                }, 2000);
            } else {
                throw new Error(response.message);
            }
        } catch (error: any) {
            setIsRestarting(false);
            setError(language === 'es'
                ? 'Error al cambiar estado del Sales Bot'
                : 'Error changing Sales Bot status');
            setTimeout(() => setError(null), 5000);
        }
    };

    const checkBotStatus = async () => {
        try {
            setSuccess(null);
            setError(null);
            setIsRestarting(true);
            
            // Verificar tanto el status como los servicios
            await Promise.all([
                refetchStatus(),
                checkAllServices()
            ]);
            
            setIsRestarting(false);
            setSuccess(language === 'es' ? 'Estado verificado correctamente' : 'Status checked successfully');
            setTimeout(() => setSuccess(null), 2000);
        } catch (error) {
            setIsRestarting(false);
            setError(language === 'es' ? 'Error al verificar estado' : 'Error checking status');
            setTimeout(() => setError(null), 3000);
        }
    };

    const handleSystemSettingsChange = (key: keyof SystemSettings, value: any) => {
        const newSettings = { ...systemSettings, [key]: value };
        setSystemSettings(newSettings);
        
        if (onSettingsChange) {
            onSettingsChange(newSettings);
        }
    };

    const saveSettings = async () => {
        try {
            setSuccess(language === 'es' ? 'Configuración guardada' : 'Settings saved');
            setTimeout(() => setSuccess(null), 2000);
        } catch (error) {
            setError(language === 'es' ? 'Error al guardar' : 'Error saving');
            setTimeout(() => setError(null), 3000);
        }
    };

    const resetSettings = () => {
        setSystemSettings({
            language,
            autoRefresh: true,
            globalAutoRefresh: true,
            refreshInterval: 30,
            notificationsEnabled: true,
            soundEnabled: false,
            theme: 'light'
        });
        setSuccess(language === 'es' ? 'Configuración restablecida' : 'Settings reset');
        setTimeout(() => setSuccess(null), 2000);
    };

    const handleDangerAction = (action: string) => {
        setDangerAction(action);
        setShowDangerModal(true);
    };

    const executeDangerAction = async () => {
        setShowDangerModal(false);
        setIsRestarting(true);
        
        try {
            await new Promise(resolve => setTimeout(resolve, 2000)); // Simulación
            setSuccess(language === 'es' ? 'Acción ejecutada' : 'Action executed');
            setTimeout(() => setSuccess(null), 2000);
        } catch (error) {
            setError(language === 'es' ? 'Error ejecutando acción' : 'Error executing action');
            setTimeout(() => setError(null), 3000);
        } finally {
            setIsRestarting(false);
        }
    };

    // ==================== EFFECTS ====================

    useEffect(() => {
        // Verificar servicios al montar y cada 30 segundos
        checkAllServices();
        
        const interval = setInterval(() => {
            if (systemSettings.autoRefresh) {
                checkAllServices();
            }
        }, systemSettings.refreshInterval * 1000);
        
        return () => clearInterval(interval);
    }, [systemSettings.autoRefresh, systemSettings.refreshInterval]);

    // ==================== RENDER ====================

    return (
        <div className="settings-tab">
            <style>{customStyles}</style>
            
            {/* Alertas */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-3">
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    {success}
                </Alert>
            )}

            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4>
                            <FontAwesomeIcon icon={faCog} className="me-2 text-primary" />
                            {getText('title')}
                        </h4>
                        <div className="d-flex gap-2">
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={checkAllServices}
                                disabled={isRestarting}
                            >
                                <FontAwesomeIcon icon={faSync} className={isRestarting ? 'fa-spin me-2' : 'me-2'} />
                                {getText('checkAllServices')}
                            </Button>
                            <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" size="sm">
                                    <FontAwesomeIcon icon={faTools} className="me-2" />
                                    {language === 'es' ? 'Herramientas' : 'Tools'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => handleDangerAction('export')}>
                                        <FontAwesomeIcon icon={faFileExport} className="me-2" />
                                        {getText('export')} Config
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleDangerAction('import')}>
                                        <FontAwesomeIcon icon={faFileImport} className="me-2" />
                                        {getText('import')} Config
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={() => handleDangerAction('reset')} className="text-danger">
                                        <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                        {getText('factoryReset')}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row className="g-4">
                {/* Control Principal */}
                <Col xl={8}>
                    <Card className="settings-card mb-4">
                        <Card.Header className="bg-transparent border-0">
                            <h5 className="mb-0">
                                <FontAwesomeIcon icon={faRobot} className="me-2 text-primary" />
                                {getText('mainControl')}
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table className="mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th style={{ width: '10%' }} className="text-center border-0 py-3">
                                            <FontAwesomeIcon icon={faInfoCircle} className="text-primary" />
                                        </th>
                                        <th style={{ width: '60%' }} className="border-0 py-3">
                                            {language === 'es' ? 'Opciones' : 'Options'}
                                        </th>
                                        <th style={{ width: '30%' }} className="border-0 py-3">
                                            {language === 'es' ? 'Estado / Control' : 'Status / Control'}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="text-center">
                                            <FontAwesomeIcon icon={faCircleDot} className="text-primary" />
                                        </td>
                                        <td>
                                            <strong>{getText('botAgent')}</strong>
                                            <br />
                                            <small className="text-muted">{getText('botAgentDesc')}</small>
                                        </td>
                                        <td>
                                            <Form.Check 
                                                type='switch'
                                                id='SalesOnOff'
                                                label={salesBotConfig.enabled 
                                                    ? `Sales Bot ${getText('active')}`
                                                    : isBotConnected 
                                                        ? `Sales Bot ${getText('disabled')}`
                                                        : (language === 'es' ? 'Servicio no disponible' : 'Service unavailable')}
                                                checked={salesBotConfig.enabled && isBotConnected}
                                                onChange={handleSalesBotToggle}
                                                disabled={!isBotConnected || isRestarting}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-center">
                                            <FontAwesomeIcon icon={faUserTie} className="text-info" />
                                        </td>
                                        <td>
                                            <strong>{getText('activeProfiles')}</strong>
                                            <br />
                                            <small className="text-muted">{getText('activeProfilesDesc')}</small>
                                        </td>
                                        <td>
                                            <Badge bg="primary" className="fs-6">
                                                {totalProfiles} {getText('profiles')}
                                            </Badge>
                                            <div className="mt-1">
                                                <Button variant="outline-primary" size="sm">
                                                    <FontAwesomeIcon icon={faEye} className="me-1" />
                                                    {getText('view')}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-center">
                                            <FontAwesomeIcon icon={faBullseye} className="text-warning" />
                                        </td>
                                        <td>
                                            <strong>{getText('runningCampaigns')}</strong>
                                            <br />
                                            <small className="text-muted">{getText('runningCampaignsDesc')}</small>
                                        </td>
                                        <td>
                                            <Badge bg="success" className="fs-6">
                                                {totalCampaigns} {getText('campaigns')}
                                            </Badge>
                                            <div className="mt-1">
                                                <Button variant="outline-success" size="sm">
                                                    <FontAwesomeIcon icon={faEye} className="me-1" />
                                                    {getText('view')}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-center">
                                            <FontAwesomeIcon icon={faSync} className="text-success" />
                                        </td>
                                        <td>
                                            <strong>{getText('globalAutoRefresh')}</strong>
                                            <br />
                                            <small className="text-muted">{getText('globalAutoRefreshDesc')}</small>
                                        </td>
                                        <td>
                                            <Form.Check 
                                                type='switch'
                                                id='GlobalAutoRefreshSwitch'
                                                label={systemSettings.globalAutoRefresh 
                                                    ? getText('enabled')
                                                    : getText('disabled')}
                                                checked={systemSettings.globalAutoRefresh}
                                                onChange={(e) => handleSystemSettingsChange('globalAutoRefresh', e.target.checked)}
                                            />
                                            <div className="mt-1">
                                                <Button 
                                                    variant="outline-secondary" 
                                                    size="sm"
                                                    onClick={checkBotStatus}
                                                    disabled={isRestarting}
                                                >
                                                    <FontAwesomeIcon icon={faSync} className="me-1" />
                                                    {getText('manualRefresh')}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>

                    {/* Estado de Servicios Corregido */}
                    <Card className="settings-card">
                        <Card.Header className="bg-transparent border-0">
                            <h5 className="mb-0">
                                <FontAwesomeIcon icon={faServer} className="me-2 text-primary" />
                                {getText('serviceHealth')}
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            {services.map((service, index) => (
                                <div 
                                    key={service.name} 
                                    className={`system-metric service-status-${service.status} ${index < services.length - 1 ? 'mb-3' : ''}`}
                                >
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center mb-2">
                                                <h6 className="mb-0 me-3">
                                                    <FontAwesomeIcon icon={faServer} className="me-2" />
                                                    {service.name}
                                                </h6>
                                                <div className="d-flex align-items-center">
                                                    <span className={`me-2 fw-bold ${
                                                        service.status === 'online' ? 'text-success' : 
                                                        service.status === 'offline' ? 'text-danger' : 
                                                        service.status === 'checking' ? 'text-info' : 'text-warning'
                                                    }`}>
                                                        {service.status === 'online' ? getText('online') : 
                                                         service.status === 'offline' ? getText('offline') :
                                                         service.status === 'checking' ? getText('checking') : getText('error')}
                                                    </span>
                                                    <div 
                                                        className={`status-indicator bg-${
                                                            service.status === 'online' ? 'success' : 
                                                            service.status === 'offline' ? 'danger' : 
                                                            service.status === 'checking' ? 'info' : 'warning'
                                                        } ${service.status === 'checking' ? 'status-checking' : ''}`} 
                                                        style={{ 
                                                            width: '12px', 
                                                            height: '12px',
                                                            animation: service.status === 'offline' ? 'blink 1s infinite' : 'none'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="small text-muted mb-2">
                                                <strong>{getText('endpoint')}:</strong> 
                                                <code className="ms-1">{service.endpoint}</code>
                                            </div>
                                            
                                            {service.message && (
                                                <div className="small mb-2">
                                                    <FontAwesomeIcon 
                                                        icon={
                                                            service.status === 'online' ? faCheckCircle :
                                                            service.status === 'checking' ? faSpinner :
                                                            faTimesCircle
                                                        } 
                                                        className={`me-1 ${
                                                            service.status === 'online' ? 'text-success' :
                                                            service.status === 'checking' ? 'text-info' :
                                                            'text-danger'
                                                        }`}
                                                    />
                                                    {service.message}
                                                </div>
                                            )}
                                            
                                            {service.details && (
                                                <div className="service-details">
                                                    <strong>{getText('details')}:</strong>
                                                    <div className="mt-1">
                                                        {typeof service.details === 'object' ? (
                                                            Object.entries(service.details).map(([key, value]) => (
                                                                <div key={key} className="d-flex justify-content-between">
                                                                    <span>{key}:</span>
                                                                    <span>{typeof value === 'boolean' ? (value ? '✓' : '✗') : String(value)}</span>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <span>{String(service.details)}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-end ms-3">
                                            <div className="small text-muted">
                                                {getText('lastChecked')}: <br />
                                                {formatDate(service.lastCheck)}
                                            </div>
                                            {service.responseTime && (
                                                <div className="small text-success mt-1">
                                                    {getText('responseTime')}: <br />
                                                    <strong>{service.responseTime}ms</strong>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </Col>
                
                {/* Panel Lateral */}
                <Col xl={4}>
                    {/* Información del Servidor */}
                    <Card className="settings-card mb-4">
                        <Card.Header className="bg-transparent border-0">
                            <h5 className="mb-0">
                                <FontAwesomeIcon icon={faNetworkWired} className="me-2 text-primary" />
                                {getText('serviceStatus')}
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Sales Bot API:</span>
                                    <div className="d-flex align-items-center">
                                        <span className={`me-2 ${isBotConnected ? 'text-success' : 'text-danger'}`}>
                                            {isBotConnected ? getText('online') : getText('offline')}
                                        </span>
                                        <div 
                                            className={`status-indicator ${isBotConnected ? 'bg-success' : 'bg-danger'}`} 
                                            style={{ 
                                                width: '12px', 
                                                height: '12px', 
                                                borderRadius: '50%',
                                                animation: isBotConnected ? 'none' : 'blink 1s infinite'
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>{getText('port')}:</span>
                                    <code>{salesBotConfig.port}</code>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>{getText('server')}:</span>
                                    <code>10.10.5.1</code>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>{getText('version')}:</span>
                                    <Badge bg="info">{salesBotConfig.version}</Badge>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span>{getText('lastSync')}:</span>
                                    <small className="text-muted">{formatDate(salesBotConfig.lastSync)}</small>
                                </div>
                            </div>
                            <hr />
                            <div className="d-grid gap-2">
                                <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    onClick={checkBotStatus}
                                    disabled={isRestarting}
                                >
                                    <FontAwesomeIcon icon={faSync} className={`me-2 ${isRestarting ? 'fa-spin' : ''}`} />
                                    {getText('checkStatus')}
                                </Button>
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    onClick={checkAllServices}
                                    disabled={isRestarting}
                                >
                                    <FontAwesomeIcon icon={faServer} className="me-2" />
                                    {getText('refreshData')}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Estadísticas Rápidas */}
                    <Card className="settings-card mb-4">
                        <Card.Header className="bg-transparent border-0">
                            <h5 className="mb-0">
                                <FontAwesomeIcon icon={faChartBar} className="me-2 text-primary" />
                                {getText('quickStats')}
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="text-center mb-3">
                                <h3 className="mb-0 text-primary">{totalConversations}</h3>
                                <small className="text-muted">{getText('activeConversations')}</small>
                            </div>
                            <div className="text-center mb-3">
                                <h3 className="mb-0 text-success">{totalProducts}</h3>
                                <small className="text-muted">{getText('activeProducts')}</small>
                            </div>
                            <div className="text-center mb-3">
                                <h3 className="mb-0 text-warning">{totalProfiles}</h3>
                                <small className="text-muted">{getText('workingProfiles')}</small>
                            </div>
                            <div className="text-center">
                                <h3 className="mb-0 text-info">{totalCampaigns}</h3>
                                <small className="text-muted">{getText('runningCampaignsShort')}</small>
                            </div>
                            
                            <hr className="my-3" />
                            
                            {/* Métricas del Bot */}
                            <div className="small">
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Bot Conversations:</span>
                                    <Badge bg="primary">{botConversations.length}</Badge>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Active Chats:</span>
                                    <Badge bg="success">{botConversations.filter(c => c.isActive).length}</Badge>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span>Closed Sales:</span>
                                    <Badge bg="info">{botConversations.filter(c => c.stage === 'closed').length}</Badge>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Configuración del Sistema */}
                    <Card className="settings-card mb-4">
                        <Card.Header className="bg-transparent border-0">
                            <h5 className="mb-0">
                                <FontAwesomeIcon icon={faDatabase} className="me-2 text-primary" />
                                {getText('systemSettings')}
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        <FontAwesomeIcon icon={faGlobe} className="me-2" />
                                        {getText('systemLanguage')}
                                    </Form.Label>
                                    <Form.Select 
                                        value={systemSettings.language}
                                        onChange={(e) => handleSystemSettingsChange('language', e.target.value)}
                                    >
                                        <option value="es">🇪🇸 Español</option>
                                        <option value="en">🇺🇸 English</option>
                                    </Form.Select>
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        <FontAwesomeIcon icon={faClock} className="me-2" />
                                        {getText('refreshInterval')}
                                    </Form.Label>
                                    <Form.Select 
                                        value={systemSettings.refreshInterval}
                                        onChange={(e) => handleSystemSettingsChange('refreshInterval', parseInt(e.target.value))}
                                    >
                                        <option value={15}>15 {getText('seconds')}</option>
                                        <option value={30}>30 {getText('seconds')}</option>
                                        <option value={60}>60 {getText('seconds')}</option>
                                        <option value={120}>120 {getText('seconds')}</option>
                                    </Form.Select>
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Check 
                                        type="switch"
                                        id="notifications"
                                        label={getText('notifications')}
                                        checked={systemSettings.notificationsEnabled}
                                        onChange={(e) => handleSystemSettingsChange('notificationsEnabled', e.target.checked)}
                                    />
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Check 
                                        type="switch"
                                        id="sound"
                                        label={getText('sound')}
                                        checked={systemSettings.soundEnabled}
                                        onChange={(e) => handleSystemSettingsChange('soundEnabled', e.target.checked)}
                                    />
                                </Form.Group>
                                
                                <hr />
                                
                                <div className="d-flex gap-2">
                                    <Button variant="primary" onClick={saveSettings} size="sm">
                                        <FontAwesomeIcon icon={faSave} className="me-2" />
                                        {getText('save')}
                                    </Button>
                                    <Button variant="outline-secondary" onClick={resetSettings} size="sm">
                                        <FontAwesomeIcon icon={faUndo} className="me-2" />
                                        {getText('reset')}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>

                    {/* Zona de Peligro */}
                    <div className="danger-zone">
                        <h6 className="text-danger mb-3">
                            <FontAwesomeIcon icon={faWarning} className="me-2" />
                            {getText('dangerZone')}
                        </h6>
                        <p className="small text-muted mb-3">
                            {language === 'es' 
                                ? 'Estas acciones son irreversibles. Procede con precaución.'
                                : 'These actions are irreversible. Proceed with caution.'}
                        </p>
                        <div className="d-grid gap-2">
                            <Button 
                                variant="outline-warning" 
                                size="sm"
                                onClick={() => handleDangerAction('resetSystem')}
                            >
                                <FontAwesomeIcon icon={faUndo} className="me-2" />
                                {getText('resetSystem')}
                            </Button>
                            <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => handleDangerAction('clearDatabase')}
                            >
                                <FontAwesomeIcon icon={faDatabase} className="me-2" />
                                {getText('clearDatabase')}
                            </Button>
                            <Button 
                                variant="danger" 
                                size="sm"
                                onClick={() => handleDangerAction('factoryReset')}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                {getText('factoryReset')}
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Modal de Confirmación para Acciones Peligrosas */}
            <Modal show={showDangerModal} onHide={() => setShowDangerModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="text-danger">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        {language === 'es' ? 'Confirmar Acción Peligrosa' : 'Confirm Dangerous Action'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        <FontAwesomeIcon icon={faWarning} className="me-2" />
                        <strong>
                            {language === 'es' ? '¡Atención!' : 'Warning!'}
                        </strong>
                        <br />
                        {language === 'es' 
                            ? `Esta acción (${dangerAction}) es irreversible y puede causar pérdida de datos.`
                            : `This action (${dangerAction}) is irreversible and may cause data loss.`}
                    </Alert>
                    <p>
                        {language === 'es' 
                            ? '¿Estás seguro de que deseas continuar?'
                            : 'Are you sure you want to continue?'}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDangerModal(false)}>
                        {language === 'es' ? 'Cancelar' : 'Cancel'}
                    </Button>
                    <Button variant="danger" onClick={executeDangerAction}>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        {language === 'es' ? 'Confirmar' : 'Confirm'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SettingsTab;

/*
==================== 📋 HEALTH CHECK CORREGIDO ====================

🔧 ENDPOINTS VERIFICADOS:

✅ WHATSAPP BOT SERVICE:
- Endpoint: /api/whatsapp/bot/status
- Verifica: success, botConnected, botEnabled, message
- Respuesta esperada: 
  {
    "success": true,
    "message": "API del bot de WhatsApp funcionando correctamente",
    "botConnected": { type: "md", ws: {...}, ev: {...}, ... },
    "botEnabled": true
  }

✅ SELLERBOT SERVICE:
- Endpoint: /api/sellerbot/status  
- Verifica: success, data.enabled, data.version, etc.
- Respuesta esperada:
  {
    "success": true,
    "data": {
      "enabled": true,
      "version": "1.0.0",
      "activeProfiles": 3,
      "runningCampaigns": 2
    }
  }

✅ LLM SERVICE:
- Endpoint: https://backend.anubisai.net/health
- Verificación básica de disponibilidad

🚀 CARACTERÍSTICAS IMPLEMENTADAS:

✅ HEALTH CHECK INTELIGENTE:
- Estados: online, offline, checking, error
- Verificación automática cada 30s (configurable)
- Timeout de 10s por servicio
- Manejo de errores robusto
- Detalles de respuesta mostrados

✅ INDICADORES VISUALES:
- Estado "checking" con animación de pulso
- Colores dinámicos por estado
- Iconos específicos (✓, ✗, ⏳, ⚠️)
- Tiempo de respuesta mostrado

✅ PANEL DE DETALLES:
- Endpoint consultado
- Mensaje de respuesta
- Detalles técnicos expandibles
- Última verificación con timestamp

✅ CONFIGURACIÓN AVANZADA:
- Intervalo de verificación configurable
- Auto-refresh activable/desactivable
- Verificación manual individual
- Verificación masiva de todos los servicios

El componente ahora consulta correctamente los endpoints reales y muestra
información detallada del estado de cada servicio con los datos esperados.
*/